import {
  MEMBER_INVOICES_ROUTE,
  isInvoicesPath,
  isModuleVisibleForStanding,
} from "@/app/layout/restricted-shell";
import {
  ADMIN_FINANCIAL_MODULE_MANIFEST,
  MODULE_MANIFESTS,
  isAdminFinancialPath,
} from "@/app/router/module-manifests";
import { resolveActiveRealmContext } from "@/core/auth/realm";
import { canAccessModule } from "@/core/auth/role-access";
import { routePathById } from "@/app/router/route-manifest";
import { matchesRouterPath } from "@/app/router/router-path";
import type { IconName } from "@/shared/components/icons/icon-map";
import type { MemberStanding, ModuleManifest, RoleClaim } from "@/types/clm-web";

export interface NavigationEntry {
  key: string;
  href: `/${string}`;
  label: string;
  icon: IconName;
  group: ModuleManifest["navigationGroup"];
  isActive: (pathname: string) => boolean;
}

export function getVisibleNavigationManifests(
  roleClaims: readonly RoleClaim[],
  standing?: MemberStanding,
): readonly ModuleManifest[] {
  return MODULE_MANIFESTS.filter(
    (manifest) =>
      manifest.protected &&
      canAccessModule(manifest, roleClaims) &&
      isModuleVisibleForStanding(manifest.moduleId, standing),
  );
}

/** Exact `base` or a path beneath it — never a sibling that merely shares the prefix. */
function isAtOrBelow(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`);
}

/** The location pathname at or below `base`, read as the router reads it (decoded, any letter case). */
function isRouteAtOrBelow(pathname: string, base: string): boolean {
  return matchesRouterPath(`${base}/*`, pathname);
}

const MY_COURSES_PATH = routePathById("my-courses");

/**
 * 004-6 T048/T049: the learner's My Courses entry follows the Courses module
 * entry on the standard realm when that module is visible to the session (so a
 * restricted standing hides both) AND the session carries `member` — the
 * module admits `courses:view` alone, but `/my-courses` requires `member`
 * (app-router), so a staff-only session gets no link it cannot open. It is
 * part of the courses module and lives on its own route base.
 */
function createMyCoursesEntry(courses: ModuleManifest): NavigationEntry {
  return {
    key: "my-courses",
    href: MY_COURSES_PATH,
    label: "My Courses",
    icon: courses.navIcon as IconName,
    group: courses.navigationGroup,
    isActive: (pathname) => isRouteAtOrBelow(pathname, MY_COURSES_PATH),
  };
}

/**
 * Module entries are active on a segment boundary of their route base. The
 * admin realm's "Financial" entry lives under `/admin/financial`, so the
 * "Admin" entry yields that subtree to it: exactly one sidebar link carries
 * `aria-current="page"` (006-9 UI contract §8a.1, financial#39 item 9).
 */
function isModuleActive(manifest: ModuleManifest, pathname: string): boolean {
  if (matchesRouterPath("/", pathname)) {
    return manifest.moduleId === "dashboard";
  }
  if (!isRouteAtOrBelow(pathname, manifest.routeBase)) {
    return false;
  }
  const financialBase = ADMIN_FINANCIAL_MODULE_MANIFEST.routeBase;
  const isAncestorOfFinancial =
    manifest.routeBase !== financialBase && isAtOrBelow(financialBase, manifest.routeBase);
  return !(isAncestorOfFinancial && isAdminFinancialPath(pathname));
}

/**
 * Shell navigation = module manifests visible to this session plus the
 * shell-owned "My invoices" entry (FR-014), which every signed-in
 * standard-realm member gets regardless of standing. The admin realm instead
 * gets the "Financial" entry of the admin-realm financial module, linking to
 * its staff Invoices surface (006-9 UI contract §8a.1) when the session holds
 * an operator claim.
 */
export function getVisibleNavigationEntries(
  roleClaims: readonly RoleClaim[],
  standing?: MemberStanding,
  realm = resolveActiveRealmContext().realm,
): readonly NavigationEntry[] {
  const entries: NavigationEntry[] = getVisibleNavigationManifests(roleClaims, standing).flatMap(
    (manifest) => {
      const entry: NavigationEntry = {
        key: manifest.moduleId,
        href: manifest.routeBase,
        label: manifest.displayName,
        icon: manifest.navIcon as IconName,
        group: manifest.navigationGroup,
        isActive: (pathname) => isModuleActive(manifest, pathname),
      };
      return realm === "standard" &&
        manifest.moduleId === "courses" &&
        roleClaims.includes("member")
        ? [entry, createMyCoursesEntry(manifest)]
        : [entry];
    },
  );

  if (realm === "standard") {
    entries.push({
      key: MEMBER_INVOICES_ROUTE.id,
      href: MEMBER_INVOICES_ROUTE.path,
      label: MEMBER_INVOICES_ROUTE.label,
      icon: "payment",
      group: "member",
      isActive: isInvoicesPath,
    });
  }

  if (realm === "admin" && canAccessModule(ADMIN_FINANCIAL_MODULE_MANIFEST, roleClaims)) {
    const [invoices] = ADMIN_FINANCIAL_MODULE_MANIFEST.deliveredSurfaces ?? [];
    if (invoices) {
      // Labelled by module displayName like every other module entry; the
      // href goes straight to the delivered Invoices surface.
      entries.push({
        key: `${ADMIN_FINANCIAL_MODULE_MANIFEST.moduleId}-${invoices.id}`,
        href: invoices.route,
        label: ADMIN_FINANCIAL_MODULE_MANIFEST.displayName,
        icon: (invoices.icon ?? ADMIN_FINANCIAL_MODULE_MANIFEST.navIcon) as IconName,
        group: ADMIN_FINANCIAL_MODULE_MANIFEST.navigationGroup,
        isActive: isAdminFinancialPath,
      });
    }
  }

  return entries;
}

export function groupNavigationEntries(
  entries: readonly NavigationEntry[],
): Record<string, NavigationEntry[]> {
  return entries.reduce<Record<string, NavigationEntry[]>>((groups, entry) => {
    groups[entry.group] = [...(groups[entry.group] ?? []), entry];
    return groups;
  }, {});
}
